export interface ReferrerName {
  id: number;
  code: string;
  name: string;
}

export interface loginData {
  user: string;
  password: string;
}

export interface basicData {
  company_name: string;
  company_address: string;
  state: string;
  city: string;
  phone_number: string;
  email: string;
  legal_entity: string;
  activity: string;
  iva_status: string;
  date_incorporation: string;
  economic_group: string;
  legal_company_address: string;
  legal_city: string;
  legal_state: string;
}

export interface corporateStructureData {
  id: number;
  person_id: number;
  ci: string;
  first_name: string;
  last_name: string;
  ownership_interest: string;
  position: string;
  assets: string | undefined;
  assets_file_path?: string | undefined;
  value: number;
}

export interface documentData {
  file: string;
  file_name: string;
  type: string;
  is_basic_data: boolean;
}

export interface cloudDocument {
  type: string;
  fullUrl: string;
  label: string;
  documentId: string;
}
export interface clientData {
  id: string;
  cuit: string;
  last_comment: string;
  basic_data: basicData;
  corporate_structure: Array<corporateStructureData>;
  files: Array<documentData>;
  products: Array<applicationData>;
  verified: boolean;
  manual_total_amount: boolean;
  requested_amount: Array<amount>;
  extra_comments: string;
  reason: string;
  review: string;
  has_valid_corporate_composition: boolean;
  signers: signersData[];
  referrer_name: string | ReferrerName;
  approved_amount?: Array<amount>;
  approved_products?: Array<applicationData>;
  id_application?: number;
  application_files: Array<applicationFile>;
  note_amount: null | number;
  guarantee_amount: null | number;
  instrumentation_id?: string;
  officer: string;
  category_type?: {
    id: number;
    name: string;
  };
  category_type_id?: number;
  effective_date?: string;
  credit_type_id?: number;
  credit_type_name?: string;
  balance_processing_status?: string | null;
  balance_processing_error_code?: string | null;
  balance_processing_error_context?: string | null;
}
export interface applicationData {
  id: number;
  product_line_code: string;
  amount: amount;
}
export interface applicationInfo {
  products: Array<applicationData>;
  manual_total_amount: boolean;
  requested_amount: Array<amount>;
  reason: string;
  review: string;
  extra_comments: string;
  has_valid_corporate_composition: boolean;
  signers: Array<signersData>;
  category_type_id?: number;
}
export interface amount {
  currency_code: string;
  value: number | string;
}

export interface commentData {
  status_code: string;
  comment: string;
  approved_products?: {
    products: applicationData[];
    manual_approved_total_amount: number;
  };
}

export interface postBalanceSheetData {
  id: number;
  monthId: string | number | null;
  amount: amount;
}

export interface riskData {
  id: number;
  risk_code: string;
  execution_date: string;
  last_execution?: string;
  indicators: Array<indicator>;
  error?: {
    code: number;
    message: string;
  };
  totals?: {
    total_current: string;
    total_non_current: string;
    total_net_worth: string;
    total_net_worth_and_non_current: string;
    gross_result: string;
    operating_result: string;
    ordinary_result: string;
    result_before_taxes: string;
    taxes: string;
  };
  debts?: any;
}

export interface indicator {
  id: number;
  code: string;
  description: string;
  value: string;
  status: string;
  tooltip_message: string;
  document?: string;
  assets?: string;
  amount?: string | number;
  total_value?: number;  // Valor total desde el backend (snake_case)
  ownership_interest?: number;  // Porcentaje de participación desde el backend (snake_case)
}

// Interfaces para la estructura estandarizada de balance
export interface BalanceTotals {
  total_current: string;
  total_non_current: string;
  total_net_worth: string;
  total_net_worth_and_non_current: string;
  gross_result: string;
  operating_result: string;
  ordinary_result: string;
  result_before_taxes: {
    extraordinary_income: string;
    extraordinary_expenses: string;
    total: number;
  };
  result_before_taxes_calculated: number;
  taxes: string;
}

export interface BalanceValues {
  id: number;
  cuit: string;
  code: string;
  from_month: number;
  from_year: number;
  to_month: number;
  to_year: number;
  current_assets: {
    current_investments: number;
    cash_and_cash_equivalents: number;
    trade_receivables_short_term: number;
    other_receivables: number;
    inventory_short_term: number;
    other_current_assets: number;
  };
  non_current_assets: {
    trade_receivables_long_term: number;
    other_receivables: number;
    permanent_investments: number;
    property_plant_and_equipment: number;
    other_non_current_assets: number;
  };
  current_liabilities: {
    trade_payables_short_term: number;
    bank_and_financial_debt_short_term: number;
    social_security_debt_short_term: number;
    tax_liabilities_short_term: number;
    other_debts_provisions: number;
    projections: number;
    other_liabilities: number;
  };
  non_current_liabilities: {
    non_current_liabilities: number;
    trade_payables_long_term: number;
    bank_and_financial_debt_long_term: number;
    social_security_debt_long_term: number;
    tax_liabilities_long_term: number;
    other_long_term_liabilities: number;
  };
  non_controlling_interest_result: {
    non_controlling_interest_result: number;
  };
  net_worth: {
    capital: number;
    other_components: number;
  };
  gross_result: {
    agricultural_production: number;
    production_cost: number;
    sales: number;
    cost_of_sales: number;
  };
  operating_result: {
    commercialization_expenses: number;
    administrative_expenses: number;
    other_income: number;
    other_expenses: number;
  };
  ordinary_result: {
    financial_expenses: number;
    exchange_difference_and_holding_result: number;
  };
  result_before_taxes: {
    extraordinary_income: number;
    extraordinary_expenses: number;
  };
  taxes: {
    income_tax: number;
  };
}

export interface StandardizedBalanceData {
  values: BalanceValues;
  totals: BalanceTotals;
}

export interface balanceInfo {
  id: number;
  from_month: number;
  from_year: number;
  to_month: number;
  to_year: number;
  current_assets: {
    current_investments: string;
    cash_and_cash_equivalents: string;
    trade_receivables_short_term: string;
    other_receivables: string;
    inventory_short_term: string;
    other_current_assets: string;
  };
  non_current_assets: {
    trade_receivables_long_term: string;
    other_receivables: string;
    permanent_investments: string;
    property_plant_and_equipment: string;
    other_non_current_assets: string;
  };
  current_liabilities: {
    trade_payables_short_term: string;
    bank_and_financial_debt_short_term: string;
    social_security_debt_short_term: string;
    tax_liabilities_short_term: string;
    other_debts_provisions: string;
    projections: string;
    other_liabilities: string;
  };
  non_current_liabilities: {
    non_current_liabilities: string;
    trade_payables_long_term: string;
    bank_and_financial_debt_long_term: string;
    social_security_debt_long_term: string;
    tax_liabilities_long_term: string;
    other_long_term_liabilities: string;
  };
  non_controlling_interest_result: {
    non_controlling_interest_result: string;
  };
  net_worth: {
    capital: string;
    other_components: string;
  };
  gross_result: {
    agricultural_production: string;
    production_cost: string;
    sales: string;
    cost_of_sales: string;
  };
  operating_result: {
    commercialization_expenses: string;
    administrative_expenses: string;
    other_income: string;
    other_expenses: string;
  };
  ordinary_result: {
    financial_expenses: string;
    exchange_difference_and_holding_result: string;
  };
  result_before_taxes: {
    extraordinary_income: string;
    extraordinary_expenses: string;
  };
  taxes: {
    income_tax: string;
  };
}

export interface salesPurchasesCloudData {
  month: number;
  year: number;
  amount: number;
}
export interface ITableData {
  size: number;
  page: number;
  totalElements: number;
  totalPages: number;
}

export interface signersData {
  locality: string;
  cuit: string;
  name: string;
  email: string;
  asset_file_path: string;
  address: string
  role: string
  signer_type_code: string[]; // Multiple types support
  position?: string;
}

export interface IItem {
  cuit: string;
  id: number;
  commercial: {
    first_name: string;
    last_name: string;
  };
  company_name: string;
  producer: {
    first_name: string;
    last_name: string;
  };
  updated_date: string;
  status: {
    id: number;
    code: string;
  };
  total_ars: number;
  total_usd: number;
  approved_total_ars: number;
  approved_total_usd: number;
  usuario?: string;
  referido?: string;
  balance_sheet_code?: string | null;
  balance_processing_status?: string | null;
  balance_processing_error_code?: string | null;
  balance_processing_error_context?: string | null;
}

export type Nullable<T> = T | null;

export interface applicationFile {
  file_type: string;
  asset_file_path: string;
}
export interface contractData {
  template_name: string;
  id: string;
  external_service_id: string;
  asset_file_path: string;
  book_location: string;
  signature_type_code: string;
  status_code: string;
  isLocal?: boolean;
}

export interface manualContractData {
  template_name: string | undefined;
  asset_file_path: string;
  book_location: string;
}

export enum InstrumentationStatus {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED',
  RETRYING = 'RETRYING'
}

export interface InstrumentationError {
  code: string;
  message: string;
  // Nota: retryable se deriva del código usando función helper
  // timestamp se obtiene de InstrumentationProgress.completed_at cuando status = FAILED
}

export interface InstrumentationProgress {
  status: InstrumentationStatus;
  started_at?: string;
  completed_at?: string;
  error?: InstrumentationError;
  duration?: number;
}

export interface Line {
  amount: amount;
  id: number;
  instrumentation_id: string | null;
  product_line_code: string;
  instrumentation_progress?: InstrumentationProgress;
}

export interface ApplicationListData {
  id: number;
  cuit: string;
  company_name: string;
  contact_email: string;
  contact_phone: string;
  status?: {
    id: number;
    code: string;
    label: string;
  } | null;
  producer?: {
    id: number;
    first_name: string;
    last_name: string;
  } | null;
  commercial?: {
    id: number;
    first_name: string;
    last_name: string;
  } | null;
  updated_date: string;
  total_ars: number;
  total_usd: number;
  manual_total_amount: boolean;
  reason: string;
  review: string;
  extra_comments: string;
  risk_analysis: any;
  approved_total_ars: number;
  approved_total_usd: number;
  officer?: string;
  referido?: string;
}

export interface ApprovedApplicationsData {
  cuit: string;
  company_name: string;
  referred_name?: string;
  total_approved_amount_ars: number;
  total_approved_amount_usd: number;
  approved_applications_count: number;
  used_amount_ars: number;
  used_amount_usd: number;
  available_amount_ars: number;
  available_amount_usd: number;
}

export interface ApprovedApplicationsResponse {
  content: ApprovedApplicationsData[];
  page: number;
  size: number;
  total_elements: number;
  total_pages: number;
}
